import type { UserStatus } from '@/api/admin-types'
import type { PermissionCode } from '@/lib/permissions'

import { ADMIN_PERMISSIONS } from './permissions'

/** Every status this client can set. `Unknown` is a read-side fallback, never a chosen state. */
export const SETTABLE_USER_STATUSES = [
  'Active',
  'Locked',
  'Disabled',
] as const satisfies readonly Exclude<UserStatus, 'Unknown'>[]

export const userStatusLabel = (status: UserStatus): string => {
  switch (status) {
    case 'Active':
      return 'Active'
    case 'Locked':
      return 'Locked'
    case 'Disabled':
      return 'Disabled'
    default:
      return 'Unknown'
  }
}

/** Long enough that "fixed" or "asked" is refused as not actually saying anything. */
export const MINIMUM_REASON_LENGTH = 5

/**
 * What administration is catalogued to do and cannot yet — this module's version of
 * `LEDGER_CAPABILITY_GAPS` and `REPORTS_CAPABILITY_GAPS`. Read before assuming a screen's absence
 * means nobody built it rather than that the API has nothing behind it yet.
 */
export interface AdminCapabilityGap {
  title: string
  permission: PermissionCode
  detail: string
}

export const ADMIN_CAPABILITY_GAPS: AdminCapabilityGap[] = [
  {
    title: 'Approval policies have no screen',
    permission: ADMIN_PERMISSIONS.approvalPolicyManage,
    detail:
      'admin.approval_policy.manage and its countersigning code are catalogued permissions, and the approvals module already recognises "Approval policy" as a request type — but the published contract has no endpoint to list, read, or edit a policy row (its request type, dual-control setting, threshold, or effective dates). There is nothing for this client to call, so no table is built rather than one showing rows this application invented.',
  },
  {
    title: 'No branch register',
    permission: ADMIN_PERMISSIONS.branchManage,
    detail:
      'admin.branch.view, admin.branch.manage and its countersigning code are catalogued, but the only branches endpoint the contract publishes is GET /access/me/branches — "the branches the signed-in user may operate in", not an institution-wide register. Administration → Branches reads that endpoint and says plainly that this is what it is showing.',
  },
  {
    title: 'Defining a new role has no screen',
    permission: ADMIN_PERMISSIONS.roleManage,
    detail:
      'POST /access/roles exists, is dual-controlled, and is implemented in admin-api.ts — but this pass only screens listing roles, reading one’s permissions, and amending them. Starting a role from nothing is not built.',
  },
  {
    title: 'No holidays, system parameters, access recertification, or segregation-of-duties override',
    permission: ADMIN_PERMISSIONS.holidayManage,
    detail:
      'admin.holiday.manage, admin.parameter.view / .manage(.approve), admin.access.review, and admin.sod.override(.approve) are all catalogued permissions with no backing endpoint anywhere in the published contract.',
  },
]
